import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Mail, UserPlus } from 'lucide-react';
import { invitationAPI } from '../../services/api';

export const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setError('Invitation token is missing.');
        setLoading(false);
        return;
      }

      try {
        const response = await invitationAPI.getByToken(token);
        setInvitation(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load invitation');
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handleAccept = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await invitationAPI.accept({ token, phone });
      setAccepted(true);
      setMessage(response.data.message || 'Invitation accepted successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-blue-100 flex items-center justify-center p-6">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-4 rounded-2xl bg-blue-100 text-blue-700">
            <UserPlus size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Join Company Workspace</h1>
            <p className="text-gray-600 mt-1">
              Accept your invitation to join the ERP system.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-600">Loading invitation...</p>
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 mt-0.5" size={20} />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        ) : accepted ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="text-green-600 mt-0.5" size={20} />
              <p className="text-green-800 text-sm">{message}</p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-white hover:bg-gray-800 transition"
            >
              Continue to login
            </Link>
          </div>
        ) : (
          <>
            <div className="grid gap-4 mb-6">
              <div className="rounded-xl border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Invited User</p>
                <p className="font-semibold text-gray-900">{invitation?.fullName}</p>
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Mail size={14} />
                  {invitation?.email}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-semibold text-gray-900">{invitation?.company?.companyName}</p>
                <p className="text-sm text-gray-600">
                  Roles: {(Array.isArray(invitation?.roles) ? invitation.roles : []).join(', ') || 'Pending assignment'}
                </p>
              </div>
            </div>

            <form onSubmit={handleAccept} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional"
                />
              </div>

              {message && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 transition"
              >
                {submitting ? 'Accepting...' : 'Accept Invitation'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

